import { PA_STATUS_BUCKET_OPTIONS } from "../../../../constants/oncologySubmission";
import { PA_STATUS_AND_BUCKET_MAPPING } from "../../../../constants/oncologySubmissionMapping";
import { BadgeOption } from "../../../../constants/submissionSummaryOptions";
import { PaStatus } from "../../../../enums/paStatus";

/**
 * Retrieves the available PA Status Bucket options based on the selected PA Status value.
 *
 * This function implements a cascading dropdown logic where the available
 * PA Status Bucket options depend on the selected PA Status.
 *
 * @param {PaStatus} paStatus - The selected PA Status value
 *        (APPROVED, DENIED, PENDING_WITH_INSURANCE, AUTH_NOT_REQUIRED, ONSHORE_ASSISTANCE, or NA_OUTCOME)
 * @returns {BadgeOption[]} An array of badge options for the PA Status Bucket dropdown.
 *                          Returns empty array for statuses with no predefined buckets.
 *                          Returns the full PA_STATUS_BUCKET_OPTIONS if no matching mapping is found.
 *
 * @example
 * // Get bucket options when PA status is "Approved"
 * const options = getNestedOptionsForPaStatusBucket(PaStatus.APPROVED);
 * // Returns: []
 *
 * @example
 * // Get bucket options when PA status is "Denied"
 * const options = getNestedOptionsForPaStatusBucket(PaStatus.DENIED);
 * // Returns: [{ id: "denial_on_file", ... }, { id: "not_covered_plan_exclusion", ... }, ...]
 */
export const getNestedOptionsForPaStatusBucket = (
  paStatus: PaStatus,
): BadgeOption[] => {
  switch (paStatus) {
    case PaStatus.APPROVED:
      return PA_STATUS_AND_BUCKET_MAPPING[PaStatus.APPROVED];
    case PaStatus.DENIED:
      return PA_STATUS_AND_BUCKET_MAPPING[PaStatus.DENIED];
    case PaStatus.PENDING:
      return PA_STATUS_AND_BUCKET_MAPPING[PaStatus.PENDING];
    case PaStatus.AUTH_NOT_REQUIRED:
      return PA_STATUS_AND_BUCKET_MAPPING[PaStatus.AUTH_NOT_REQUIRED];
    case PaStatus.ONSHORE_ASSISTANCE:
      return PA_STATUS_AND_BUCKET_MAPPING[PaStatus.ONSHORE_ASSISTANCE];
    case PaStatus.NA_OUTCOME:
      return PA_STATUS_AND_BUCKET_MAPPING[PaStatus.NA_OUTCOME];
    default:
      return PA_STATUS_BUCKET_OPTIONS;
  }
};

/**
 * Resolves `pa_outcome_classification` for Astera save payloads and initial form state.
 * Returns an empty string when the selected PA status has no bucket options.
 *
 * @input paStatus - Selected PA status value
 * @input caseStatus - Selected PA status reason / bucket id
 * @return Empty string when buckets do not apply, otherwise caseStatus
 */
export const resolvePaOutcomeClassificationForSave = (
  paStatus: PaStatus | string,
  caseStatus: string,
): string => {
  const bucketOptions = getNestedOptionsForPaStatusBucket(paStatus as PaStatus);
  return bucketOptions.length === 0 ? "" : caseStatus;
};
